import React, { useState, useEffect, useRef, useMemo } from "react";
import { ChevronDown, X, Search, MapPin, Layers, Building2 } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { viewAllCity } from "../../Redux/Actions/settingsActions";
import "./cityDropDown.css";

const CityBusinessFilterDropdown = ({
    cityValue,
    businessValue,
    projectValue,
    onCityChange,
    onBusinessChange,
    onProjectChange,
}) => {
    const dispatch = useDispatch();
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [activeTab, setActiveTab] = useState("city");
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);

    const { citiesList } = useSelector((state) => state.city || {});
    const cityData = citiesList?.data ?? [];
    const hasFetchedCities = useRef(false);

    const businessOptions = [
        // { value: "", label: "All" },
        { value: "fresh", label: "Fresh" },
        { value: "resale", label: "Resale" },
        { value: "business", label: "Business" },
        { value: "rental", label: "Rental" },
    ];

    const projectOptions = [
        // { value: "", label: "All" },
        { value: "project", label: "Project" },
        { value: "property", label: "Property" },
        { value: "rental_property", label: "Rental Property" },
    ];

    useEffect(() => {
        if (!cityData.length && !hasFetchedCities.current) {
            dispatch(viewAllCity());
            hasFetchedCities.current = true;
        }
    }, [dispatch, cityData.length]);

    useEffect(() => {
        if (!businessValue) {
            onBusinessChange({ target: { value: "fresh" } });
        }

        if (!projectValue) {
            onProjectChange({ target: { value: "project" } });
        }
    }, [businessValue, projectValue, onBusinessChange, onProjectChange]);

    const cityOptions = useMemo(() => {
        return cityData.map((city) => ({
            value: city._id,
            label: city.city_name,
        }));
    }, [cityData]);

    const filteredCities = useMemo(() => {
        return cityOptions.filter((option) =>
            option.label.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [cityOptions, searchTerm]);

    const selectedCity = useMemo(() => {
        return cityOptions.find((opt) => opt.value === cityValue);
    }, [cityOptions, cityValue]);

    const selectedBusiness = useMemo(() => {
        return businessOptions.find((opt) => opt.value === businessValue);
    }, [businessValue]);

    const selectedProject = useMemo(() => {
        return projectOptions.find((opt) => opt.value === projectValue);
    }, [projectValue]);

    const handleCitySelect = (val) => {
        onCityChange({ target: { value: val } });
        setIsOpen(false);
        setSearchTerm("");
    };

    const handleBusinessSelect = (val) => {
        onBusinessChange({ target: { value: val } });
        setIsOpen(false);
    };

    const handleProjectSelect = (val) => {
        onProjectChange({ target: { value: val } });
        setIsOpen(false);
    };

    const handleClearCity = (e) => {
        e.stopPropagation();
        onCityChange({ target: { value: "" } });
    };

    const handleClearBusiness = (e) => {
        e.stopPropagation();
        onBusinessChange({ target: { value: "" } });
    };

    const handleClearProject = (e) => {
        e.stopPropagation();
        onProjectChange({ target: { value: "" } });
    };

    useEffect(() => {
        if (!isOpen) return;

        const handleEvents = (event) => {
            if (event.type === "mousedown") {
                if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                    setIsOpen(false);
                    setSearchTerm("");
                }
            }
            if (event.type === "keydown" && event.key === "Escape") {
                setIsOpen(false);
                setSearchTerm("");
            }
        };

        document.addEventListener("mousedown", handleEvents);
        document.addEventListener("keydown", handleEvents);

        if (activeTab === "city") {
            setTimeout(() => searchInputRef.current?.focus(), 100);
        }

        return () => {
            document.removeEventListener("mousedown", handleEvents);
            document.removeEventListener("keydown", handleEvents);
        };
    }, [isOpen, activeTab]);

    const hasFilters = cityValue || businessValue || projectValue;

    return (
        <>
            {isOpen && <div className="dropdown-backdrop" />}

            <div className="city-dropdown-container" ref={dropdownRef}>
                <button
                    className="city-dropdown-trigger"
                    onClick={() => setIsOpen(!isOpen)}
                    aria-expanded={isOpen}
                    type="button"
                    title={[selectedCity?.label, selectedBusiness?.label, selectedProject?.label].filter(Boolean).join(" · ")}
                >
                    <MapPin size={18} className="trigger-icon" />
                    <span className="trigger-label">Filters</span>

                    {hasFilters && (
                        <span
                            style={{
                                background: "#3b82f6",
                                color: "#fff",
                                borderRadius: "999px",
                                minWidth: "18px",
                                height: "18px",
                                fontSize: "11px",
                                fontWeight: 600,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "0 5px",
                            }}
                        >
                            {[cityValue, businessValue, projectValue].filter(Boolean).length}
                        </span>
                    )}

                    <ChevronDown
                        size={20}
                        className={`trigger-chevron ${isOpen ? "open" : ""}`}
                    />
                </button>

                {isOpen && (
                    <div className="city-dropdown-popups">
                        <div className="popup-header">
                            <h3 className="popup-title">Filters</h3>
                            <button
                                className="popup-close"
                                onClick={() => setIsOpen(false)}
                                aria-label="Close"
                                type="button"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div style={{ display: "flex", borderBottom: "1px solid #e2e8f0" }}>
                            <button
                                type="button"
                                onClick={() => setActiveTab("city")}
                                style={{
                                    flex: 1,
                                    padding: "12px",
                                    background: "transparent",
                                    border: "none",
                                    borderBottom:
                                        activeTab === "city" ? "2px solid #3b82f6" : "2px solid transparent",
                                    color: activeTab === "city" ? "#3b82f6" : "#64748b",
                                    fontWeight: activeTab === "city" ? 600 : 400,
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "6px",
                                    fontSize: "14px",
                                    transition: "all 0.2s ease",
                                }}
                            >
                                <MapPin size={16} />
                                City
                                {cityValue && (
                                    <span
                                        style={{
                                            background: "#3b82f6",
                                            color: "#fff",
                                            borderRadius: "50%",
                                            width: "18px",
                                            height: "18px",
                                            fontSize: "11px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        1
                                    </span>
                                )}
                            </button>

                            <button
                                type="button"
                                onClick={() => setActiveTab("business")}
                                style={{
                                    flex: 1,
                                    padding: "12px",
                                    background: "transparent",
                                    border: "none",
                                    borderBottom:
                                        activeTab === "business" ? "2px solid #3b82f6" : "2px solid transparent",
                                    color: activeTab === "business" ? "#3b82f6" : "#64748b",
                                    fontWeight: activeTab === "business" ? 600 : 400,
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "6px",
                                    fontSize: "14px",
                                    transition: "all 0.2s ease",
                                }}
                            >
                                <Layers size={16} />
                                Business Type
                                {businessValue && (
                                    <span
                                        style={{
                                            background: "#3b82f6",
                                            color: "#fff",
                                            borderRadius: "50%",
                                            width: "18px",
                                            height: "18px",
                                            fontSize: "11px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        1
                                    </span>
                                )}
                            </button>

                            <button
                                type="button"
                                onClick={() => setActiveTab("project")}
                                style={{
                                    flex: 1,
                                    padding: "12px",
                                    background: "transparent",
                                    border: "none",
                                    borderBottom:
                                        activeTab === "project" ? "2px solid #3b82f6" : "2px solid transparent",
                                    color: activeTab === "project" ? "#3b82f6" : "#64748b",
                                    fontWeight: activeTab === "project" ? 600 : 400,
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "6px",
                                    fontSize: "14px",
                                    transition: "all 0.2s ease",
                                }}
                            >
                                <Building2 size={16} />
                                Project/Property
                                {projectValue && (
                                    <span
                                        style={{
                                            background: "#3b82f6",
                                            color: "#fff",
                                            borderRadius: "50%",
                                            width: "18px",
                                            height: "18px",
                                            fontSize: "11px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        1
                                    </span>
                                )}
                            </button>
                        </div>

                        {activeTab === "city" && (
                            <>
                                <div className="search-container">
                                    <Search size={18} className="search-icon" style={{ top: "18px", left: "20px" }} />
                                    <input
                                        ref={searchInputRef}
                                        type="text"
                                        className="search-input"
                                        placeholder="Search cities..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    {searchTerm && (
                                        <button
                                            className="search-clear"
                                            onClick={() => setSearchTerm("")}
                                            aria-label="Clear search"
                                            type="button"
                                        >
                                            <X size={16} />
                                        </button>
                                    )}
                                </div>

                                <div className="options-list">
                                    {filteredCities.length > 0 ? (
                                        filteredCities.map((option) => (
                                            <button
                                                key={option.value}
                                                className={`option-item ${cityValue === option.value ? "selected" : ""}`}
                                                onClick={() => handleCitySelect(option.value)}
                                                type="button"
                                            >
                                                <MapPin size={16} className="option-icon" />
                                                <span className="option-text">{option.label}</span>
                                                {cityValue === option.value && <span className="option-check">✓</span>}
                                            </button>
                                        ))
                                    ) : (
                                        <div className="no-results">
                                            <MapPin size={40} className="no-results-icon" />
                                            <p className="no-results-text">No cities found</p>
                                            <p className="no-results-hint">Try a different search term</p>
                                        </div>
                                    )}
                                </div>

                                <div className="popup-footer">
                                    <span className="footer-text">
                                        {filteredCities.length} {filteredCities.length === 1 ? "city" : "cities"} available
                                    </span>
                                    {cityValue && (
                                        <button
                                            type="button"
                                            onClick={handleClearCity}
                                            style={{
                                                background: "none",
                                                border: "none",
                                                color: "#ef4444",
                                                fontSize: "13px",
                                                cursor: "pointer",
                                                fontWeight: 500,
                                            }}
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>
                            </>
                        )}

                        {activeTab === "business" && (
                            <>
                                <div className="options-list">
                                    {businessOptions.map((option) => (
                                        <button
                                            key={option.value}
                                            className={`option-item ${businessValue === option.value ? "selected" : ""}`}
                                            onClick={() => handleBusinessSelect(option.value)}
                                            type="button"
                                        >
                                            <span style={{ fontSize: "18px" }}>{option.icon}</span>
                                            <span className="option-text">{option.label}</span>
                                            {businessValue === option.value && <span className="option-check">✓</span>}
                                        </button>
                                    ))}
                                </div>

                                <div
                                    className="popup-footer"
                                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                                >
                                    <span className="footer-text">
                                        {businessValue ? `Selected: ${selectedBusiness?.label}` : "No filter applied"}
                                    </span>
                                    {businessValue && (
                                        <button
                                            type="button"
                                            onClick={handleClearBusiness}
                                            style={{
                                                background: "none",
                                                border: "none",
                                                color: "#ef4444",
                                                fontSize: "13px",
                                                cursor: "pointer",
                                                fontWeight: 500,
                                            }}
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>
                            </>
                        )}

                        {activeTab === "project" && (
                            <>
                                <div className="options-list">
                                    {projectOptions.map((option) => (
                                        <button
                                            key={option.value}
                                            className={`option-item ${projectValue === option.value ? "selected" : ""}`}
                                            onClick={() => handleProjectSelect(option.value)}
                                            type="button"
                                        >
                                            <span style={{ fontSize: "18px" }}>{option.icon}</span>
                                            <span className="option-text">{option.label}</span>
                                            {projectValue === option.value && <span className="option-check">✓</span>}
                                        </button>
                                    ))}
                                </div>

                                <div
                                    className="popup-footer"
                                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                                >
                                    <span className="footer-text">
                                        {projectValue ? `Selected: ${selectedProject?.label}` : "No filter applied"}
                                    </span>
                                    {projectValue && (
                                        <button
                                            type="button"
                                            onClick={handleClearProject}
                                            style={{
                                                background: "none",
                                                border: "none",
                                                color: "#ef4444",
                                                fontSize: "13px",
                                                cursor: "pointer",
                                                fontWeight: 500,
                                            }}
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default CityBusinessFilterDropdown;